import { OfferStatus } from '@/data/common'
import { FgtsRequest, FgtsResponse } from '~/data/loans/fgts/offers'
import {
  fetchFgtsOfferById,
  fetchFgtsOffers,
  sendFgtsPostRequest,
} from '~/api/loans/fgts/offers'
import {
  JbClevertapEvent,
  JbLoanSimulationEvent,
} from '~/data/clevertap/jb-clevertap'

interface FgtsState {
  onGoingOffers: FgtsResponse[]
  grantedOffers: FgtsResponse[]
  latestSimulationRequest: FgtsRequest
  latestSimulationDate: string
}

// STATE
const state = (): FgtsState => ({
  onGoingOffers: [],
  grantedOffers: [],
  latestSimulationRequest: JSON.parse(
    localStorage.getItem('jb.state.fgts.form') ?? '{}',
  ),
  latestSimulationDate:
    localStorage.getItem('jb.state.fgts.last-simulation') ?? '',
})

// GETTERS
const getters = {
  gtAllOngoingFgtsOffers(state: FgtsState): FgtsResponse[] {
    const enabled = false
    if (!enabled) return []
    return state.onGoingOffers
  },
  gtAllGrantedFgtsOffers(state: FgtsState): FgtsResponse[] {
    return state.grantedOffers
  },
  gtFgtsLastSimulationDate(state: FgtsState): string {
    return state.latestSimulationDate
  },
  gtFgtsLatestSimulationRequest(state: FgtsState): FgtsRequest {
    return state.latestSimulationRequest
  },
}

// MUTATIONS
const mutations = {
  SET_FGTS_OFFERS(state, payload: Array<FgtsResponse | any>): void {
    payload.forEach((o) => {
      o.product_type = 'fgts'
    })
    state.onGoingOffers = payload.filter(
      (o) => o.status === OfferStatus.ONGOING,
    )
    state.grantedOffers = payload.filter(
      (o) => o.status === OfferStatus.GRANTED,
    )
  },
  SET_LATEST_SIMULATION_REQUEST(state, payload) {
    state.latestSimulationRequest = {
      ...state.latestSimulationRequest,
      ...payload,
    }
    localStorage.setItem(
      'jb.state.fgts.form',
      JSON.stringify(state.latestSimulationRequest),
    )
  },
  SET_LAST_SIMULATION_DATE(state) {
    state.latestSimulationDate = new Date().toISOString()
    localStorage.setItem(
      'jb.state.fgts.last-simulation',
      state.latestSimulationDate,
    )
  },
}

// ACTIONS
const actions = {
  async actGetFgtsOfferById(_, offer_id) {
    try {
      return await fetchFgtsOfferById(offer_id)
    } catch (e: any) {
      return e?.response
    }
  },
  async actSimulateFgtsLoan({ dispatch }, request: FgtsRequest) {
    try {
      const response = await sendFgtsPostRequest(request)
      if (response.status === 200) {
        dispatch(
          'clevertap/actCleverTapPushEvent',
          {
            eventName: JbClevertapEvent.LOAN_SIMULATION,
            eventData: {
              ProductName: 'fgts',
              LoanAmount: request.fgts_balance,
              LoanInstallments: 0,
              Platform: navigator.userAgent,
            },
          } as JbLoanSimulationEvent,
          { root: true },
        )
      }
      return response
    } catch (e: any) {
      dispatch(
        'utils/feedback/createFeedback',
        'Tivemos um probleminha ao enviar seus dados',
        { root: true },
      )
      return e?.response
    }
  },
  async actGetFgtsOffers(
    { dispatch, commit },
    options = {
      status: [
        OfferStatus.ONGOING,
        OfferStatus.FATAL_ERROR,
        OfferStatus.GRANTED,
      ],
    },
  ) {
    const errorMsg =
      'Tivemos um probleminha ao consultar novas ofertas para voce'
    try {
      const response = await fetchFgtsOffers(options)
      const { data } = response
      if (data) {
        commit('SET_FGTS_OFFERS', response?.data ?? [])
      } else {
        dispatch('utils/feedback/createFeedback', errorMsg, { root: true })
      }
      return response
    } catch (e: any) {
      dispatch('utils/feedback/createFeedback', errorMsg, { root: true })
      commit('SET_FGTS_OFFERS', [])
      return e?.response
    }
  },
  actSetFgtsLastSimulationDate({ commit }) {
    commit('SET_LAST_SIMULATION_DATE')
  },
  actSetFgtsLatestSimulationRequest({ commit }, request) {
    commit('SET_LATEST_SIMULATION_REQUEST', request)
  },
}

// export
export default { namespaced: true, state, getters, actions, mutations }
